import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort       from "@/core/services/Rpc/RpcDataSort";
import ShipmentToBuyer        from "@/domains/ShipmentsToBuyer/ShipmentToBuyer";
import Receipt                from "@/domains/Receipts/Receipt";
import Core                   from '@/core/Core';
import ShipmentsDAO           from '@/domains/Shipments/ShipmentsDAO';
import Shipment               from '@/domains/Shipments/Shipment';
import ReceiptsService        from '@/domains/Receipts/ReceiptsService';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';
import Assembly               from '@/domains/Assemblies/Assembly';
import OrderPosition          from '@/domains/Orders/OrderPosition';
import ApplicationError       from '@/core/misc/ApplicationError';
import { ErrorSeverity }      from '@/core/misc/ErrorSeverity';
import TransportOrdersDAO from "@/domains/TransportOrders/TransportOrdersDAO";

/**
 * Сервис для работы с отгрузками
 */
class ShipmentsService {

	static async get(id: number): Promise<Shipment | null> {
		const response = await ShipmentsDAO.get(id);

		return Core.utils.isObject(response.data) ? Shipment.fromServer(response.data) : null;
	}

	/**
	 * Загрузка списка отгрузок.
	 * Совместим с DataGridLoader как fetchFunction.
	 */
	static async getListPaginated(
    params?: Parameters<typeof ShipmentsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Shipment[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await ShipmentsDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, Shipment.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Загрузка списка отгрузок.
	 * Для использования вне реестра (диалоги, автокомплиты).
	 */
	static async getList(
    params?: Parameters<typeof ShipmentsDAO.getList>[0]
  ): Promise<Shipment[]> {
		return (await this.getListPaginated(params)).data;
	}

	/** ------- CRUD ------- */

	static async add(shipment: Shipment): Promise<boolean> {
		const response = await ShipmentsDAO.add(shipment);

		if (response.ok) {
			Core.showNotification.success('Отгрузка создана');
		}

		return response.ok;
	}

	static async update(shipment: Shipment, changeReasonText?: string): Promise<boolean> {
		const response = await ShipmentsDAO.update(shipment, changeReasonText);

		if (response.ok) {
			Core.showNotification.success('Отгрузка обновлена');
		}

		return response.ok;
	}

	static async delete(shipment: Shipment, changeReasonText?: string): Promise<boolean> {
		const response = await ShipmentsDAO.delete(shipment.id, changeReasonText);

		if (response.ok) {
			Core.showNotification.success('Отгрузка удалена');
		}

		return response.ok;
	}

	/**
	 * Удалить отгрузку с подтверждением.
	 * Возвращает true если удаление произошло — вызывающий сам решает что делать
	 * (обычно refresh лоадера регистра).
	 */
	static async deleteWithConfirm(shipment: Shipment): Promise<boolean> {
		const confirmed = await Core.confirm('Подтверждаете удаление отгрузки?');

		if (confirmed) {
			return await this.delete(shipment);
		} else {
			return false;
		}
	}

	/**
	 * Открывает карточку отгрузки.
	 * source — то, из чего отгрузка берётся: существующая отгрузка (открываем
	 * по id), сборка, позиции заказа, либо ничего — тогда новая с нуля.
	 *
	 * Промис резолвится по закрытию карточки — вызывающий (грид/попап) сам решает,
	 * рефрешить ли список после. Отдельные onCreated/onDeleted-колбэки не нужны:
	 * есть showAsPromise, а из событий карточка эмитит только Updated.
	 */
	static showShipmentCard(source: Shipment | Assembly | OrderPosition | ShipmentToBuyer | Receipt | OrderPosition[] | null = null): Promise<boolean> {
		if (source === null) {
			return Core.dialogs.ShipmentCard.showAsPromise({});
		}
		else if (source instanceof Shipment) {
			return Core.dialogs.ShipmentCard.showAsPromise({ id: source.id });
		}
		else if (source instanceof Receipt) {
			return Core.dialogs.ShipmentCard.showAsPromise({ id: source.shipment.id });
		}
		else if (source instanceof Assembly) {
			return Core.dialogs.ShipmentCard.showAsPromise({ assembly: source });
		}
		else if (source instanceof ShipmentToBuyer) {
			return Core.dialogs.ShipmentCard.showAsPromise({ id: source.shipmentFromSeller.id });
		}
		else if (Array.isArray(source) && source[0] instanceof OrderPosition) {
			return Core.dialogs.ShipmentCard.showAsPromise({ orderPositions: source });
		}
		else if (source instanceof OrderPosition) {
			return Core.dialogs.ShipmentCard.showAsPromise({ orderPositions: [source] });
		}
		else {
			throw new ApplicationError(
				'ShipmentsService.showShipmentCard: неизвестный источник отгрузки',
				0,
				ErrorSeverity.Error,
				{ source },
			);
		}
	}

	/**
	 * Открывает диалог истории отгрузки
	 */
	static showFeed(shipmentId: number): void {
		Core.dialogs.FeedDialog.show({
			entityTypeName: 'shipment',
			entityId: shipmentId,
			showAddComment: true,
		});
	}

	/**
	 * Контекстное меню отгрузки для VDataGridV2 (:group-context-menu).
	 * Грид сам рисует кебаб и обновляет список после действия (refreshAfter !== false).
	 *
	 * Геттер, а не статическое свойство: видимость и причины зависят от
	 * Core.currentPartner, недоступного на этапе загрузки модуля.
	 *
	 * Колбэки возвращают промисы — грид дождётся завершения перед refresh.
	 */
	static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (shipment: Shipment) => {
          return this.showShipmentCard(shipment);
        },
        (shipment: Shipment) => Shipment.reasonUnavailableEdit(shipment),
        false,
        true,
        true,
      ),
      new ContextMenuItem(
        'Принять',
        'inventory',
        (shipment: Shipment) => {
          return ReceiptsService.openReceiptCard(null, shipment)
        },
        null,
        Core.currentPartner.isSeller === true,
        true
      ),
      new ContextMenuItem(
        'Создать платёж',
        'add_card',
        (shipment: Shipment) => {
          return Core.dialogs.PaymentRequestCard.showAsPromise({shipmentOrId: shipment});
        },
        (shipment: Shipment) => Shipment.reasonUnavailableCreatePayment(shipment),
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (shipment: Shipment) => {
          this.showFeed(shipment.id);
        },
        (shipment: Shipment) => Shipment.reasonUnavailableHistory(shipment),
        false,
        false,
        true,
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (shipment: Shipment) => {
          return this.deleteWithConfirm(shipment);
        },
        (shipment: Shipment) => Shipment.reasonUnavailableDelete(shipment),
      ),
    ];
  }
}

export default ShipmentsService;
